import React, { useState, useEffect, useRef } from 'react'

import { ExplicitChangeTextInput } from '../../../common/components/explicit-change-text-input'
import { Select } from '../../../common/components/select'
import { usePageEditorContext } from '../../contexts/page-editor.context'
import { Accordion, AccordionSummary, AccordionDetails } from '../sidebar-accordion'
import { ManualBlockEditor } from '../manual-block-editor'
import { CategoryListEdit } from '../category-list-edit'
import { reportDataSetService } from '../report-data-set.service'
import { TABLE_TYPE } from '../../blocks/table/table.constants'
import { CellFormatEdit } from './cell-format-edit'
import { tableEditService } from './table-edit.service'
import { selectComponentService } from '../../../common/services/select-component.service'
import { HeaderEditAccordion } from '../header-edit-accordion'
import { FooterEditAccordion } from '../footer-edit-accordion'
import { FilterEditAccordion } from '../filter-edit-accordion'

const SECTIONLESS_ROWS_LABEL = 'Sectionless Rows'
const GROUPLESS_COLUMNS_LABEL = 'Groupless Columns'
const COLUMN_TYPE = {
  LABEL: 'labelColumnWidth',
  DATA: 'dataColumnWidth'
}
const COLUMN_WIDTH_DEFAULT = {
  [COLUMN_TYPE.LABEL]: 290,
  [COLUMN_TYPE.DATA]: 180
}

export const TableBlockEditSidebar = function ({ block }) {
  const pageEditorContext = usePageEditorContext() || {}
  block.props = block.props || {}

  const filterOptionLabels = reportDataSetService.getFilterOptionLabels(block)

  const dataKey = useRef(block.dataKey)
  const [isOtherExpanded, setIsOtherExpanded] = useState(!dataKey.current)
  const [isLabelColumnWidthInvalid, setIsLabelColumnWidthInvalid] = useState(false)
  const [isDataColumnWidthInvalid, setIsDataColumnWidthInvalid] = useState(false)
  const [variableOptions, setVariableOptions] = useState(tableEditService.getVariableOptions(pageEditorContext.page, dataKey.current))
  const sparklineVariableOptions = [ { value: undefined, label: 'None' }, ...variableOptions ]

  useEffect(() => {
    if (block.dataKey !== dataKey.current) {
      setVariableOptions(tableEditService.getVariableOptions(pageEditorContext.page, block.dataKey))
      dataKey.current = block.dataKey
    }
  }, [block.dataKey])

  function updateBlock (block) {
    pageEditorContext.registerUnsavedChanges()
    pageEditorContext.updateCurrentEdit({ block: { ...block } })
  }

  function hasParentConfig (parentConfigName) {
    return (block.props[parentConfigName] || []).length > 0
  }

  function hasColumnGroups () {
    return hasParentConfig('columnGroups')
  }

  function hasSections () {
    return hasParentConfig('sections')
  }

  function getParentLabelEditInputs (parentConfigName, childConfigName, orphanLabel) {
    const values = block.props[parentConfigName].map((parent) => ({
      value: parent.value,
      label: isEmpty(parent.value) ? orphanLabel : parent.label,
      isDisabled: isEmpty(parent.value),
      [childConfigName]: parent[childConfigName]
    }))
    return [{ values }]
  }

  function handleParentLabelChange (updated, parentConfigName, childConfigName) {
    block.props[parentConfigName] = updated[0].values.map((parent) => ({
      value: parent.value,
      label: parent.isDisabled ? '' : parent.label,
      [childConfigName]: parent[childConfigName]
    }))
    updateBlock(block)
  }

  function getChildLabelEditInputs (parentConfigName, childConfigName, orphanLabel) {
    if (hasParentConfig(parentConfigName)) {
      return block.props[parentConfigName].map((parent) => ({
        value: parent.value,
        label: isEmpty(parent.value) ? orphanLabel : parent.label,
        isEmpty: isEmpty(parent.value),
        disableReorder: isEmpty(parent.value),
        values: parent[childConfigName]
      }))
    } else {
      return [{
        values: block.props[childConfigName] || []
      }]
    }
  }

  function handleChildLabelChange (updated, parentConfigName, childConfigName) {
    if (hasParentConfig(parentConfigName)) {
      block.props[parentConfigName] = updated.map((parent) => ({
        value: parent.value,
        label: parent.isEmpty ? '' : parent.label,
        [childConfigName]: parent.values
      }))
    } else {
      block.props[childConfigName] = updated[0].values
    }
    updateBlock(block)
  }

  function getColumnWidthInputValue (prop) {
    return (block.props[prop] || COLUMN_WIDTH_DEFAULT[prop]).toString()
  }

  function handleColumnWidthChange (prop, value) {
    const setInvalid = (prop === COLUMN_TYPE.LABEL) ? setIsLabelColumnWidthInvalid : setIsDataColumnWidthInvalid
    const width = Number(value)
    if (isNaN(width)) {
      setInvalid(true)
    } else {
      // If width is different from default, set it, otherwise unset it
      block.props[prop] = (width === COLUMN_WIDTH_DEFAULT[prop]) ? undefined : width
      setInvalid(false)
      updateBlock(block)
    }
  }

  function isEmpty (value) {
    return tableEditService.isEmpty(value)
  }

  // Sidebar only for data tables
  if (block.props.subtype === TABLE_TYPE.TEXT || block.props.subtype === TABLE_TYPE.UNKNOWN) {
    return null
  }

  return <div className="report-editor-sidebar">
    {dataKey.current && <>
      <Accordion square defaultExpanded={true}>
        <AccordionSummary>
          Cell Format
        </AccordionSummary>
        <AccordionDetails>
          <div className="field-grid">
            <div className="full-width-field">
              <CellFormatEdit variableOptions={variableOptions} />
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
      {hasColumnGroups() && (
        <Accordion square>
          <AccordionSummary>
            Column Groups
          </AccordionSummary>
          <AccordionDetails>
            <div className="field-grid">
              <div className="full-width-field">
                <CategoryListEdit
                  size="medium"
                  categories={getParentLabelEditInputs('columnGroups', 'columns', GROUPLESS_COLUMNS_LABEL)}
                  hideCategoryDropdown={true}
                  hideLabelToggles={true}
                  onChange={(updated) => {
                    handleParentLabelChange(updated, 'columnGroups', 'columns')
                  }}
                />
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      )}
      <Accordion square>
        <div data-qa="accordion-summary-columns">
          <AccordionSummary>
          Columns
          </AccordionSummary>
        </div>
        <AccordionDetails>
          <div className="field-grid">
            <div className="full-width-field">
              <CategoryListEdit
                size="medium"
                categoryLabel="Column Group"
                listLabel="Column Labels"
                categories={getChildLabelEditInputs('columnGroups', 'columns', GROUPLESS_COLUMNS_LABEL)}
                hideCategoryDropdown={!hasColumnGroups()}
                hideLabelToggles={true}
                onChange={(updated) => {
                  handleChildLabelChange(updated, 'columnGroups', 'columns')
                }}
              />
            </div>
            {[
              [COLUMN_TYPE.LABEL, 'Label Column Minimum Width', isLabelColumnWidthInvalid],
              [COLUMN_TYPE.DATA, 'Data Column Width', isDataColumnWidthInvalid]
            ].map(([ prop, label, isInvalid ]) => (
              <div key={prop} className="full-width-field">
                <ExplicitChangeTextInput
                  label={label}
                  size="medium"
                  value={getColumnWidthInputValue(prop)}
                  message={isInvalid && { message: 'Column width must be a number', type: 'error' }}
                  onChange={(event) => {
                    handleColumnWidthChange(prop, event.target.value)
                  }}
                />
              </div>
            ))}
          </div>
        </AccordionDetails>
      </Accordion>
      {hasSections() && (
        <Accordion square>
          <div data-qa="accordion-summary-sections">
            <AccordionSummary>
            Sections
            </AccordionSummary>
          </div>
          <AccordionDetails>
            <div className="field-grid">
              <div className="full-width-field">
                <CategoryListEdit
                  size="medium"
                  categories={getParentLabelEditInputs('sections', 'rows', SECTIONLESS_ROWS_LABEL)}
                  hideCategoryDropdown={true}
                  hideLabelToggles={true}
                  onChange={(updated) => {
                    handleParentLabelChange(updated, 'sections', 'rows')
                  }}
                />
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      )}
      <Accordion square>
        <div data-qa="accordion-summary-rows">
          <AccordionSummary>
          Rows
          </AccordionSummary>
        </div>
        <AccordionDetails>
          <div className="field-grid">
            <div className="full-width-field">
              <CategoryListEdit
                size="medium"
                categoryLabel="Section"
                listLabel="Row Labels"
                categories={getChildLabelEditInputs('sections', 'rows', SECTIONLESS_ROWS_LABEL)}
                hideCategoryDropdown={!hasSections()}
                hideLabelToggles={true}
                onChange={(updated) => {
                  handleChildLabelChange(updated, 'sections', 'rows')
                }}
              />
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion square>
        <div data-qa="accordion-summary-sparkline">
          <AccordionSummary>
          Sparkline
          </AccordionSummary>
        </div>
        <AccordionDetails>
          <div className="field-grid">
            <div className="full-width-field" data-qa="sparkline-variable-option">
              <Select
                label="Sparkline variable"
                size="medium"
                options={sparklineVariableOptions}
                value={selectComponentService.getSelectedValue(sparklineVariableOptions, block.props.sparklineVariable)}
                onChange={({ value }) => {
                  block.props.sparklineVariable = value
                  if (value === undefined) {
                    block.props.secondaryLabel = undefined
                  }
                  updateBlock(block)
                }}
              />
            </div>
            <div className="full-width-field" data-qa="sparkline-variable-label">
              <ExplicitChangeTextInput
                label="Label"
                size="medium"
                value={block.props.secondaryLabel || ''}
                disabled={block.props.sparklineVariable === undefined}
                onChange={(event) => {
                  const value = event.target.value
                  if (value.length) {
                    block.props.secondaryLabel = value
                  } else {
                    block.props.secondaryLabel = undefined
                  }
                  updateBlock(block)
                }}
              />
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
      {filterOptionLabels.filter(({ values }) => values.length > 0).length > 0 && (
        <FilterEditAccordion block={block} />
      )}
      <HeaderEditAccordion block={block} />
      <FooterEditAccordion block={block} />
    </>}
    <Accordion square expanded={isOtherExpanded} onChange={() => { setIsOtherExpanded(!isOtherExpanded) }}>
      <AccordionSummary>
        Other
      </AccordionSummary>
      <AccordionDetails>
        <div className="field-grid">
          <div className="full-width-field">
            <ManualBlockEditor block={block} onChange={updateBlock} />
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  </div>
}
